import React from 'react';

// AdminPanel "Clients who might be stuck". GET /api/admin/stuck-clients
// decides who is flagged and why (thresholds live in the backend's
// stuck-clients.js); this renders the list so an admin can reach out.
//
// Hide / Unhide: POST /api/admin/stuck-clients/:clientId/hide|unhide sets or
// clears brand_profile.stuck_hidden_at. Hidden clients still come back from
// the GET (hidden: true) and stay hidden until unhidden, even while still
// stuck; the card leaves them out unless "Show hidden (N)" is ticked. The
// change is applied optimistically and rolled back, with an error, if the
// request fails.
//
// adminGet, adminPost and onViewAs come from AdminPanel, so requests carry the
// same admin session and View As goes through the same handleViewAs — audit
// POST included.

const DAY_MS = 24 * 60 * 60 * 1000;

const REASON_LABELS = {
  no_connections:  'No accounts connected',
  never_published: 'Nothing published yet',
};

const PLAN_LABELS = { managed: 'Managed', self_serve: 'Self-Serve' };

export function daysSince(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / DAY_MS));
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function relativeSignIn(iso, now = Date.now()) {
  const d = daysSince(iso, now);
  if (d === null) return 'never';
  if (d === 0) return 'today';
  return `${plural(d, 'day')} ago`;
}

const smallBtn = {
  background: 'transparent', color: '#aaa', border: '1px solid #555',
  borderRadius: 999, padding: '4px 12px',
  fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 12,
  flexShrink: 0,
};

export function StuckClientsCard({ adminGet, adminPost, onViewAs, viewingAsClientId, viewingAsBusy, styles }) {
  const [clients, setClients]         = React.useState([]);
  const [loading, setLoading]         = React.useState(false);
  const [error, setError]             = React.useState('');
  const [showHidden, setShowHidden]   = React.useState(false);
  const [pending, setPending]         = React.useState({});   // id → true while hide/unhide is in flight
  const [actionError, setActionError] = React.useState('');

  const fetchStuck = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminGet('/api/admin/stuck-clients');
      setClients(data.clients || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    fetchStuck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchClient = (id, patch) =>
    setClients(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));

  const setHidden = async (client, hide) => {
    const before = { hidden: Boolean(client.hidden), stuck_hidden_at: client.stuck_hidden_at ?? null };
    setActionError('');
    setPending(p => ({ ...p, [client.id]: true }));
    patchClient(client.id, { hidden: hide, stuck_hidden_at: hide ? new Date().toISOString() : null });
    try {
      const data = await adminPost(`/api/admin/stuck-clients/${encodeURIComponent(client.id)}/${hide ? 'hide' : 'unhide'}`);
      patchClient(client.id, { hidden: hide, stuck_hidden_at: data?.stuck_hidden_at ?? null });
    } catch (err) {
      patchClient(client.id, before);
      setActionError(`Couldn't ${hide ? 'hide' : 'unhide'} ${client.brand_name || client.id}: ${err.message}`);
    }
    setPending(p => { const next = { ...p }; delete next[client.id]; return next; });
  };

  const now = Date.now();
  const hiddenCount = clients.filter(c => c.hidden).length;
  const activeCount = clients.length - hiddenCount;
  const shown       = showHidden ? clients : clients.filter(c => !c.hidden);

  return (
    <div style={styles.card} data-testid="stuck-clients-card">
      <h3 style={styles.cardTitle}>
        Clients who might be stuck
        {activeCount > 0 && (
          <span style={{
            marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px',
            borderRadius: 10, background: '#ffb02022', border: '1px solid #ffb020', color: '#ffb020',
          }}>{activeCount}</span>
        )}
      </h3>

      {loading && <p style={styles.meta}>Loading…</p>}
      {error && <p style={styles.errorText}>{error}</p>}
      {actionError && <p style={styles.errorText} role="alert">{actionError}</p>}
      {!loading && !error && activeCount === 0 && (
        <p style={styles.meta}>Nobody looks stuck right now.</p>
      )}

      {hiddenCount > 0 && (
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10,
          fontFamily: 'Barlow, sans-serif', fontSize: 12, color: '#aaa', cursor: 'pointer',
        }}>
          <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} />
          Show hidden ({hiddenCount})
        </label>
      )}

      {shown.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {shown.map(c => {
            const age     = daysSince(c.created_at, now);
            const viewing = viewingAsClientId === c.id;
            const busy    = viewingAsBusy === c.id;
            const saving  = Boolean(pending[c.id]);
            return (
              <div
                key={c.id}
                data-testid="stuck-client-row"
                data-hidden={c.hidden ? 'true' : 'false'}
                style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                  padding: '10px 18px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.04)', border: `1px ${c.hidden ? 'dashed #555' : 'solid #ffb020'}`,
                  fontFamily: 'Barlow, sans-serif', fontSize: 13, color: '#fff',
                  opacity: c.hidden ? 0.6 : 1,
                }}
              >
                <span style={{ ...styles.dot, background: '#ffb020' }} />
                <strong style={{ minWidth: 140 }}>{c.brand_name || c.id}</strong>
                <span style={{ color: '#aaa' }}>{PLAN_LABELS[c.plan] || 'no plan'}</span>

                {(c.reasons || []).map(r => (
                  <span
                    key={r}
                    style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: '#ffb02022', border: '1px solid #ffb020', color: '#ffb020',
                    }}
                  >
                    {REASON_LABELS[r] || r}{age !== null ? ` · ${plural(age, 'day')}` : ''}
                  </span>
                ))}

                <span style={{ color: '#555' }}>|</span>
                <span style={{ color: '#aaa' }}>{plural(c.photos || 0, 'photo')}</span>
                <span style={{ color: '#555' }}>|</span>
                <span style={{ color: '#aaa' }}>Last sign-in {relativeSignIn(c.last_sign_in_at, now)}</span>

                <button
                  onClick={() => onViewAs(c)}
                  disabled={busy || viewing}
                  style={{
                    marginLeft: 'auto',
                    background: viewing ? 'transparent' : '#31DCBA',
                    color:      viewing ? '#31DCBA' : '#000',
                    border:     viewing ? '1px solid #31DCBA' : 'none',
                    borderRadius: 999, padding: '4px 14px',
                    fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 12,
                    cursor: (busy || viewing) ? 'default' : 'pointer',
                    opacity: busy ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                >
                  {viewing ? 'Viewing' : busy ? '…' : 'View as'}
                </button>
                <button
                  onClick={() => setHidden(c, !c.hidden)}
                  disabled={saving}
                  style={{ ...smallBtn, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
                >
                  {c.hidden ? 'Unhide' : 'Hide'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <button onClick={fetchStuck} style={{ ...styles.btnTeal, marginTop: 16 }}>↻ Refresh</button>
    </div>
  );
}

export default StuckClientsCard;
